import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AccessTime from '@material-ui/icons/AccessTime';
import DateRange from '@material-ui/icons/DateRange';
import LocationOn from '@material-ui/icons/LocationOn';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import arrowBottomIcon from '@/assets/Icons/ic_arrow_bottom.svg';

import { Context as AuthContext } from '@/context/AuthContext.js';

import { dayjs } from '@/utils/dayjsSetup.js';
import { createQuestionObj, getText } from '@/utils/helpers.js';

import { useBookingStore, usePersistedPrismicStore, useSearchStore } from '@/store.js';

import TextfieldQuestion from '../CustomAnamnesis/QuestionTypes/TextfieldQuestion.jsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import ChainMetaIcon from './chain-meta-icon.tsx';
import ChainOverviewCard from './chain-overview-card.tsx';
import ChainPostBookingInfo from './chain-post-booking-info.tsx';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        position: 'relative',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '8rem',
        padding: '2rem 0',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        position: 'relative',
    },
    sectionHeading: {
        color: theme.palette.common.darkGrey,
        textTransform: 'uppercase',
        opacity: 0.5,
        fontSize: '14px',
    },
    dataContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        [theme.breakpoints.down('xs')]: {
            gap: '1.5rem',
        },
    },
    dataEntry: {
        display: 'flex',
        alignItems: 'center',
        gap: '1.25rem',
        '& > p:first-child': {
            width: '12rem',
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'start',
            gap: '0.5rem',
        },
    },
    patientGrid: {
        display: 'grid',
        // minmax(0, 1fr) rather than 1fr: a plain 1fr cannot shrink below its content, so a long
        // email widened its column and pulled the account fields left of the contact fields below.
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: '1.5rem',
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    patientField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
    },
    patientFieldValue: {
        fontWeight: theme.weight.heading,
        overflowWrap: 'anywhere',
    },
    appointmentDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '1rem 0',
    },
    appointmentServiceName: {
        ...theme.typography.HL3,
    },
    appointmentMetaRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        color: theme.palette.common.darkGrey,
    },
    appointmentMetaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    practiceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.875rem',
        color: theme.palette.common.midGrey,
    },
    infoAccordion: {
        boxShadow: 'none',
        border: 'none',
        borderBottom: `1px solid ${theme.palette.common.inputDisabledGray}`,
        borderRadius: 0,
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
        },
    },
    infoAccordionSummary: {
        padding: '0',
        '&.Mui-expanded': {
            minHeight: '48px',
        },
    },
    infoAccordionSummaryContent: {
        '&.Mui-expanded': {
            margin: '12px 0',
        },
    },
    infoAccordionDetails: {
        padding: '0',
    },
}));

const ConfirmAppointment = () => {
    const { t, i18n } = useTranslation();
    const {
        state: { user, userAddresses },
    } = useContext(AuthContext);
    const classes = useStyles();
    const {
        [i18n.language]: { mainData: prismicData },
    } = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const {
        [i18n.language]: { benefits: registerPrismicData },
    } = usePersistedPrismicStore((state) => state.registerData);
    const {
        [i18n.language]: { content: accountPrismicData },
    } = usePersistedPrismicStore((state) => state.accountData);
    const loading = useBookingStore((state) => state.loading);
    const selectedTreatments = useSearchStore((state) => state.selectedTreatments);
    const treatments = useSearchStore((state) => state.treatments);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const locations = useSearchStore((state) => state.locations);
    const selectedTimeslot = useBookingStore((state) => state.selectedTimeslot);
    const additionalInfo = useBookingStore((state) => state.additionalInfo);
    const selectedChainConfig = useSearchStore((state) => state.selectedChainConfig);
    const chainAvailability = useSearchStore((state) => state.chainAvailability);

    const isChainOverview = Boolean(selectedChainConfig);

    // Match the suggestions screen: resolve the Prismic treatment label, falling back to the raw key.
    const resolveStepLabel = (key) =>
        (selectedLocation ? getText(treatments?.[selectedLocation]?.[key]?.name) : undefined) ?? key;

    const isOverlayVisible = loading || !user || !userAddresses;

    if (isOverlayVisible) {
        return (
            <div className={classes.loadingContainer}>
                <EternoSpinner />
            </div>
        );
    }

    const patientSections = (
        <>
            <div className={classes.section}>
                <Typography className={classes.sectionHeading}>
                    {prismicData?.account_information_summary[0]?.text}
                </Typography>
                <div className={classes.patientGrid}>
                    <div className={classes.patientField}>
                        <Typography>{accountPrismicData.name[0].text}</Typography>
                        <Typography className={classes.patientFieldValue}>
                            {`${user?.name.first} ${user?.name.last}`}
                        </Typography>
                    </div>
                    <div className={classes.patientField}>
                        <Typography>{registerPrismicData.email_label}</Typography>
                        <Typography className={classes.patientFieldValue}>{user?.email}</Typography>
                    </div>
                    <div className={classes.patientField}>
                        <Typography>{registerPrismicData.date_of_birth.split('(')[0]}</Typography>
                        <Typography className={classes.patientFieldValue}>
                            {dayjs(user?.date_of_birth).format('DD.MM.YYYY')}
                        </Typography>
                    </div>
                    <div className={classes.patientField}>
                        {/* eslint-disable-next-line no-underscore-dangle */}
                        <Typography>{registerPrismicData.mobile_number__optional_}</Typography>
                        <Typography className={classes.patientFieldValue}>{user?.phone.number}</Typography>
                    </div>
                </div>
            </div>
            <div className={classes.section}>
                <Typography className={classes.sectionHeading}>
                    {prismicData?.contact_details_summary[0]?.text}
                </Typography>
                <div className={classes.patientGrid}>
                    <div className={classes.patientField}>
                        <Typography>{t('Address')}</Typography>
                        <div>
                            <Typography className={classes.patientFieldValue}>
                                {`${userAddresses?.street} ${userAddresses?.house_number}, ${userAddresses?.zip} ${userAddresses?.city}`}
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.patientField}>
                        <Typography>{accountPrismicData.country_label[0].text}</Typography>
                        <Typography className={classes.patientFieldValue}>{userAddresses?.country.name}</Typography>
                    </div>
                </div>
            </div>
            <Accordion defaultExpanded className={classes.infoAccordion} classes={{ root: classes.infoAccordion }}>
                <AccordionSummary
                    expandIcon={
                        <img src={arrowBottomIcon} alt="" style={{ width: 16, height: 10, objectFit: 'contain' }} />
                    }
                    aria-controls="additional-info-content"
                    id="additional-info-header"
                    classes={{
                        root: classes.infoAccordionSummary,
                        content: classes.infoAccordionSummaryContent,
                    }}
                >
                    <Typography>{prismicData.booking_summary.info_question_text[0].text}</Typography>
                </AccordionSummary>
                <AccordionDetails id="additional-info-content" classes={{ root: classes.infoAccordionDetails }}>
                    <TextfieldQuestion
                        question={createQuestionObj(
                            '',
                            prismicData.booking_summary.info_placeholder_text[0].text,
                            'additional-info'
                        )}
                        answer={additionalInfo}
                        setAnswer={(value) =>
                            value.length <= 300 && useBookingStore.setState({ additionalInfo: value })
                        }
                        hideTitle
                        multiline
                        maxRows={2}
                        height="4.75rem"
                    />
                </AccordionDetails>
            </Accordion>
        </>
    );

    // Chain overview: TERMINDETAILS card + patient sections + post-booking info. The overlay covers the whole
    // overview (card, patient sections, and post-booking info) while booking processes.
    if (isChainOverview) {
        return (
            <div className={classes.container}>
                <ChainOverviewCard
                    items={chainAvailability}
                    noBorder
                    selectedChainConfig={selectedChainConfig}
                    resolveStepLabel={resolveStepLabel}
                    practiceName={getText(locations?.[selectedLocation]?.title) ?? ''}
                    practiceAddress={getText(locations?.[selectedLocation]?.address)}
                />
                <div className={classes.container}>{patientSections}</div>
                <ChainPostBookingInfo />
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.section}>
                <div className={classes.appointmentDetails}>
                    <Typography variant="h2" className={classes.appointmentServiceName}>
                        {Object.keys(selectedTreatments)
                            .filter((treatmentKey) => selectedTreatments[treatmentKey])
                            .map((treatmentKey) => treatments[selectedLocation]?.[treatmentKey].name[0].text)
                            .join(', ')}
                    </Typography>
                    <div className={classes.appointmentMetaRow}>
                        <span className={classes.appointmentMetaItem}>
                            <ChainMetaIcon icon={DateRange} />
                            <Typography variant="body1" component="span">
                                {dayjs(selectedTimeslot).format('dddd, DD MMM YYYY')}
                            </Typography>
                        </span>
                        <Typography variant="body1" component="span" aria-hidden="true">
                            ·
                        </Typography>
                        <span className={classes.appointmentMetaItem}>
                            <ChainMetaIcon icon={AccessTime} />
                            <Typography variant="body1" component="span">
                                {dayjs(selectedTimeslot).format('HH:mm')}
                            </Typography>
                        </span>
                    </div>
                    <div className={classes.practiceRow}>
                        <ChainMetaIcon icon={LocationOn} />
                        <Typography variant="inherit" component="span">
                            {locations[selectedLocation].title[0].text}
                        </Typography>
                    </div>
                </div>
            </div>
            {patientSections}
        </div>
    );
};

export default ConfirmAppointment;
